import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import API, { formatPrice } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Package, 
  Clock, 
  Upload, 
  CheckCircle, 
  Image, 
  XCircle, 
  Search, 
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  FileDown,
  SplitSquareVertical
} from "lucide-react";
import { toast } from "sonner";

const ORDERS_PER_PAGE = 10;

function POCountdown({ closingDate }) {
  const { t } = useLanguage();
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!closingDate) return;
    const target = new Date(closingDate).getTime();
    
    const update = () => {
      const now = new Date().getTime();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft("");
        return;
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);
      
      let parts = [];
      if (d > 0) parts.push(`${d}${t("orders.unit_day")}`);
      if (h > 0 || d > 0) parts.push(`${h}${t("orders.unit_hour")}`);
      parts.push(`${m}${t("orders.unit_minute")}`);
      parts.push(`${s}${t("orders.unit_second")}`);
      
      setTimeLeft(parts.join(" "));
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [closingDate, t]);

  if (!timeLeft) return null;

  return (
    <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-700 bg-amber-100/50 px-2 py-0.5 rounded border border-amber-200/50 mt-1 w-fit animate-pulse-subtle">
      <Clock className="w-3 h-3" />
      <span className="uppercase tracking-wider">
        {t("orders.po_closing_in")} {timeLeft}
      </span>
    </div>
  );
}

const getDisplayProductLabel = (productName = "", brandName = "") => {
  const rawName = String(productName || "").trim();
  const cleanBrandName = String(brandName || "").trim();
  if (!rawName) return cleanBrandName || "Produk";
  const nameWithoutLegacyBrand = rawName.replace(/\s+by\s+.+$/i, "").trim();
  if (!cleanBrandName || cleanBrandName === "No Brand") return nameWithoutLegacyBrand || rawName;
  return `${nameWithoutLegacyBrand || rawName} by ${cleanBrandName}`;
};

const getDisplayVariantValues = (variant = {}) => {
  const technicalKeys = new Set(["variant_id", "product_id", "weight_gram", "weight", "sku", "stock", "price", "cost_price", "sort_order", "status", "source_hash", "created_at", "updated_at", "pricing"]);
  return Object.entries(variant || {})
    .filter(([key, value]) => !technicalKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== "")
    .map(([, value]) => String(value));
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const { t, locale } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [statusGroup, setStatusGroup] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [completing, setCompleting] = useState(null);
  const deepLinkHandled = useRef(false);

  // Fallback colors for RS orders not in DB yet
  const fallbackColors = {
    pending: "bg-yellow-50 text-yellow-700",
    paid: "bg-blue-50 text-blue-700",
    processing: "bg-indigo-50 text-indigo-700",
    shipped: "bg-purple-50 text-purple-700",
    completed: "bg-emerald-50 text-emerald-700",
    cancelled: "bg-red-50 text-red-700",
  };

  const getStatusDef = (code) => orderStatuses.find(s => s.code === code);

  const getStatusLabel = (code) => {
    const def = getStatusDef(code);
    if (def) return locale === "id" ? def.name_id : def.name_en;
    // Fallback i18n for RS
    switch (code) {
      case "pending": return t("orders.status_pending");
      case "paid": return t("orders.status_paid");
      case "processing": return t("orders.status_processing");
      case "shipped": return t("orders.status_shipped");
      case "completed": return t("orders.status_completed");
      case "cancelled": return t("orders.status_cancelled");
      default: return code;
    }
  };

  const getStatusColor = (code) => {
    const def = getStatusDef(code);
    if (def) return def.color;
    return fallbackColors[code] || "bg-stone-100 text-stone-700";
  };

  const poPaymentStatusMap = {
    unpaid: { id: "Belum Bayar", en: "Unpaid", color: "bg-amber-100 text-amber-800" },
    partially_paid: { id: "Bayar Sebagian", en: "Partially Paid", color: "bg-blue-100 text-blue-800" },
    waiting_verification_dp: { id: "Menunggu Verifikasi DP", en: "Waiting DP Verification", color: "bg-blue-600 text-white" },
    dp_paid: { id: "Sudah DP", en: "Deposit Paid", color: "bg-sky-100 text-sky-800" },
    waiting_verification_settlement: { id: "Menunggu Verifikasi Pelunasan", en: "Waiting Settlement Verification", color: "bg-blue-600 text-white" },
    paid: { id: "Lunas", en: "Paid", color: "bg-emerald-100 text-emerald-800" },
    cancelled: { id: "Dibatalkan", en: "Cancelled", color: "bg-red-100 text-red-800" },
  };
  const readyStockPaymentStatusMap = {
    unpaid: { id: "Menunggu Pembayaran", en: "Waiting Payment", color: "bg-amber-100 text-amber-800" },
    partially_paid: { id: "Bayar Sebagian", en: "Partially Paid", color: "bg-blue-100 text-blue-800" },
    paid: { id: "Lunas", en: "Paid", color: "bg-emerald-100 text-emerald-800" },
    cancelled: { id: "Dibatalkan", en: "Cancelled", color: "bg-red-100 text-red-800" },
  };

  const orderProgressMap = {
    pending: { id: "Menunggu Pembayaran", en: "Waiting Payment", color: "bg-stone-100 text-stone-700" },
    waiting_po_close: { id: "Menunggu PO Close", en: "Waiting PO Close", color: "bg-yellow-100 text-yellow-800" },
    po_closed: { id: "PO Ditutup", en: "PO Closed", color: "bg-orange-100 text-orange-800" },
    sent_to_central: { id: "Dikirim ke Pusat", en: "Sent to Central", color: "bg-cyan-100 text-cyan-800" },
    in_production: { id: "Dalam Produksi", en: "In Production", color: "bg-indigo-100 text-indigo-800" },
    ready_at_central: { id: "Ready di Pusat", en: "Ready at Central", color: "bg-violet-100 text-violet-800" },
    ready_at_gallery: { id: "Ready di Gallery", en: "Ready at Gallery", color: "bg-fuchsia-100 text-fuchsia-800" },
    ready_to_ship: { id: "Siap Kirim", en: "Ready to Ship", color: "bg-cyan-100 text-cyan-800" },
    processing: { id: "Diproses", en: "Processing", color: "bg-cyan-100 text-cyan-800" },
    shipped: { id: "Dikirim", en: "Shipped", color: "bg-purple-100 text-purple-800" },
    completed: { id: "Selesai", en: "Completed", color: "bg-emerald-600 text-white" },
    cancelled: { id: "Dibatalkan", en: "Cancelled", color: "bg-red-100 text-red-800" },
  };

  const isPreorderOrder = (order) => Boolean(order?.payment_summary?.is_preorder) || (order?.items || []).some((item) => item?.stock_type === "preorder" || item?.product?.stock_type === "preorder");
  const getPaymentStatusMeta = (code, order = null) => {
    const map = isPreorderOrder(order) ? poPaymentStatusMap : readyStockPaymentStatusMap;
    return map[code] || { id: code || "-", en: code || "-", color: "bg-stone-100 text-stone-700" };
  };
  const getOrderProgressMeta = (code) => orderProgressMap[code] || { id: code || "-", en: code || "-", color: "bg-stone-100 text-stone-700" };
  const getPrimaryStatusMeta = (order) => {
    const paymentCode = order.payment_status_v2 || "";
    const orderCode = order.order_status_v2 || "";
    if (["waiting_verification_dp", "waiting_verification_settlement"].includes(paymentCode)) {
      return getPaymentStatusMeta(paymentCode, order);
    }
    if (order.has_pending_confirmation) {
      return { id: "Menunggu Verifikasi Pembayaran", en: "Waiting Payment Verification", color: "bg-blue-600 text-white" };
    }
    if (orderCode) {
      return getOrderProgressMeta(orderCode);
    }
    if (paymentCode) {
      return getPaymentStatusMeta(paymentCode, order);
    }
    return { id: getStatusLabel(order.status), en: getStatusLabel(order.status), color: getStatusColor(order.status) };
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const [r, sRes] = await Promise.all([
        API.get("/orders"),
        API.get("/order-statuses").catch(() => ({ data: { statuses: [] } }))
      ]);
      setOrderStatuses(sRes.data.statuses || []);
      const fetchedOrders = r.data.orders || [];
      
      // Fetch installments for each order sequentially or in parallel
      const ordersWithInstallments = await Promise.all(
        fetchedOrders.map(async (o) => {
          try {
            const instRes = await API.get(`/installments/${o.id}`);
            return { ...o, installments: instRes.data.installments || [] };
          } catch {
            return { ...o, installments: [] };
          }
        })
      );
      
      setOrders(ordersWithInstallments);
    } catch {
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchOrders(); }, []);

  const handleUploadProof = async (orderId, file) => {
    setUploading(orderId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await API.post(`/orders/${orderId}/payment-proof`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("orders.success_upload"));
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || (locale === "id" ? "Gagal unggah" : "Upload failed")); }
    finally { setUploading(null); }
  };

  const hasShipmentAwb = (order) => Array.isArray(order.shipments) && order.shipments.some((s) => !!s.awb);
  const isShippingSetupComplete = (order) => {
    const shipments = order.shipments || [];
    if (!shipments.length || !(order.items || []).length) return false;
    const shipmentMap = new Map(shipments.map((s) => [s.id, s]));
    const allItemsAllocated = (order.items || []).every((item) => item.shipment_id && shipmentMap.has(item.shipment_id));
    return allItemsAllocated && shipments.every((s) =>
      !!String(s.name || '').trim() &&
      !!String(s.phone || '').trim() &&
      !!String(s.address || '').trim() &&
      !!String(s.destination_id || '').trim() &&
      !!String(s.courier || '').trim() &&
      !!String(s.shipping_service || '').trim() &&
      Number(s.shipping_cost || 0) > 0
    );
  };
  const matchesStatusGroup = (order, group) => {
    const paymentCode = order.payment_status_v2 || "";
    const orderCode = order.order_status_v2 || "";

    if (group === "all") return true;
    if (group === "unpaid") return paymentCode === "unpaid";
    if (group === "partially_paid") return paymentCode === "partially_paid";
    if (group === "waiting_verification_dp") return paymentCode === "waiting_verification_dp";
    if (group === "dp_paid") return paymentCode === "dp_paid";
    if (group === "waiting_po_close") return orderCode === "waiting_po_close";
    if (group === "po_closed") return orderCode === "po_closed";
    if (group === "sent_to_central") return orderCode === "sent_to_central";
    if (group === "in_production") return orderCode === "in_production";
    if (group === "ready_goods") return ["ready_at_central", "ready_at_gallery"].includes(orderCode);
    if (group === "waiting_verification_settlement") return paymentCode === "waiting_verification_settlement";
    if (group === "ready_to_ship") return orderCode === "ready_to_ship";
    if (group === "shipped") return orderCode === "shipped";
    if (group === "completed") return orderCode === "completed";
    if (group === "cancelled") return orderCode === "cancelled" || paymentCode === "cancelled";
    return true;
  };
  const statusGroupOptions = [
    { value: "all", label: "Semua" },
    { value: "unpaid", label: "Belum Bayar" },
    { value: "partially_paid", label: "Bayar Sebagian" },
    { value: "waiting_verification_dp", label: "Menunggu Verifikasi DP" },
    { value: "dp_paid", label: "Sudah DP" },
    { value: "waiting_po_close", label: "Menunggu PO Close" },
    { value: "po_closed", label: "PO Ditutup" },
    { value: "sent_to_central", label: "Dikirim ke Pusat" },
    { value: "in_production", label: "Dalam Produksi" },
    { value: "ready_goods", label: "Ready Barang" },
    { value: "waiting_verification_settlement", label: "Menunggu Verifikasi Pelunasan" },
    { value: "ready_to_ship", label: "Siap Kirim" },
    { value: "shipped", label: "Dikirim" },
    { value: "completed", label: "Selesai" },
    { value: "cancelled", label: "Dibatalkan" },
  ];
  const statusGroupCounts = statusGroupOptions.reduce((acc, opt) => {
    acc[opt.value] = orders.filter((order) => matchesStatusGroup(order, opt.value)).length;
    return acc;
  }, {});

  const filteredOrders = orders
    .filter((o) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        o.id.toLowerCase().includes(searchLower) ||
        o.items?.some(item => 
          item.product_name.toLowerCase().includes(searchLower) ||
          (item.brand_name || "").toLowerCase().includes(searchLower)
        )
      );
      return matchesSearch && matchesStatusGroup(o, statusGroup);
    })
    .sort((a, b) => {
      if (sortBy === "name_asc" || sortBy === "name_desc") {
        const nameA = a.items?.[0]?.product_name || "";
        const nameB = b.items?.[0]?.product_name || "";
        return sortBy === "name_asc" 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      const dateA = new Date(a.transaction_date || a.created_at);
      const dateB = new Date(b.transaction_date || b.created_at);
      return sortBy === "newest" ? dateB - dateA : dateA - dateB;
    });
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
  const pageStartIndex = (currentPage - 1) * ORDERS_PER_PAGE;
  const paginatedOrders = filteredOrders.slice(pageStartIndex, pageStartIndex + ORDERS_PER_PAGE);
  const pageEndIndex = Math.min(pageStartIndex + paginatedOrders.length, filteredOrders.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, statusGroup]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Deep-link: jump to the right page, scroll to, and highlight order when order_id is in URL
  useEffect(() => {
    if (deepLinkHandled.current || loading || filteredOrders.length === 0) return;
    const targetOrderId = searchParams.get("order_id");
    if (!targetOrderId) return;

    const targetIndex = filteredOrders.findIndex((order) => order.id === targetOrderId);
    if (targetIndex === -1) return;

    const targetPage = Math.floor(targetIndex / ORDERS_PER_PAGE) + 1;
    if (currentPage !== targetPage) {
      setCurrentPage(targetPage);
      return;
    }

    const el = document.querySelector(`[data-testid="order-card-${targetOrderId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedOrderId(targetOrderId);
      deepLinkHandled.current = true;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("order_id");
      setSearchParams(nextParams, { replace: true });
      setTimeout(() => setHighlightedOrderId(null), 3000);
    }
  }, [loading, filteredOrders, currentPage, searchParams, setSearchParams]);

  const handleCompleteOrder = async (orderId) => {
    if (!window.confirm(locale === "id" ? "Tandai pesanan ini sudah diterima/selesai?" : "Mark this order as received/completed?")) return;
    setCompleting(orderId);
    try {
      await API.post(`/orders/${orderId}/complete`);
      toast.success(locale === "id" ? "Pesanan ditandai selesai" : "Order marked completed");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || (locale === "id" ? "Gagal menyelesaikan pesanan" : "Failed to complete order"));
    } finally {
      setCompleting(null);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(t("orders.cancel_confirm"))) return;
    setCancelling(orderId);
    try {
      await API.post(`/orders/${orderId}/cancel`);
      toast.success(t("orders.cancel_success"));
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || (locale === "id" ? "Gagal membatalkan" : "Failed to cancel"));
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="min-h-screen" data-testid="orders-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="font-serif text-3xl text-stone-900">{t("orders.title")}</h1>
          <p className="text-sm text-stone-500">
            {filteredOrders.length} {t("orders.title").toLowerCase()}
            {filteredOrders.length > 0 && (
              <span className="block md:inline md:ml-2 text-stone-400">
                {locale === "id" ? "Menampilkan" : "Showing"} {pageStartIndex + 1}-{pageEndIndex}
              </span>
            )}
          </p>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {statusGroupOptions.map((opt) => {
            const active = statusGroup === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusGroup(opt.value)}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50"}`}
              >
                {opt.label} <span className={active ? "text-white/70" : "text-stone-400"}>({statusGroupCounts[opt.value] || 0})</span>
              </button>
            );
          })}
        </div>

        {/* Filter Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-8">
          <div className="sm:col-span-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input 
              placeholder={t("orders.search_placeholder")} 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 border-stone-200 focus:ring-stone-400"
            />
          </div>
          
          <div className="sm:col-span-4">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-11 border-stone-200 focus:ring-stone-400">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-stone-400" />
                  <SelectValue placeholder={t("orders.sort_by")} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t("orders.newest")}</SelectItem>
                <SelectItem value="oldest">{t("orders.oldest")}</SelectItem>
                <SelectItem value="name_asc">{t("orders.name_asc")}</SelectItem>
                <SelectItem value="name_desc">{t("orders.name_desc")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>


        {loading ? (
          <div className="space-y-4">{[1,2,3].map((i) => <div key={i} className="h-40 bg-stone-100 animate-pulse" />)}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-white border border-dashed border-stone-200 rounded-xl">
            <Package className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-400 font-medium">{t("orders.no_orders")}</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-20 bg-white border border-dashed border-stone-200 rounded-xl">
            <Search className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-400 font-medium">{t("common.no_products")}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {paginatedOrders.map((order) => {
              const paymentSummary = order.payment_summary || {};
              const totalPaid = paymentSummary.total_paid ?? (order.installments || []).reduce((sum, inst) => sum + inst.nominal, 0);
              const remaining = paymentSummary.outstanding_total ?? Math.max(0, order.grand_total - totalPaid);

              let shippingData = {};
              if (order.shipping && typeof order.shipping === "object") {
                shippingData = order.shipping;
              } else if (order.shipping && typeof order.shipping === "string") {
                try { shippingData = JSON.parse(order.shipping); } catch {}
              }
              const shippingCost = Number(shippingData.shipping_cost || 0);
              const orderDiscountTotal = Number(order.total_discount || 0);
              const orderTotalBeforeShipping = Math.max(0, Number(order.grand_total || order.total || 0) - shippingCost);
              const orderGrandTotalOriginal = orderTotalBeforeShipping + orderDiscountTotal;
              const anyShipmentHasAwb = hasShipmentAwb(order);
              const shippingSetupAllowedStatuses = ["in_production", "ready_at_central", "ready_at_gallery", "ready_to_ship", "shipped"];
              const shouldShowShippingSetupButton = order.payment_status_v2 !== "cancelled" && !["cancelled", "completed"].includes(order.order_status_v2) && shippingSetupAllowedStatuses.includes(order.order_status_v2) && !anyShipmentHasAwb;
              const canCompleteOrder = order.order_status_v2 === "shipped";
              const primaryStatusMeta = getPrimaryStatusMeta(order);

              return (
                <div key={order.id} className={`bg-white border transition-all duration-500 ${highlightedOrderId === order.id ? "border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/30" : "border-stone-200"}`} data-testid={`order-card-${order.id}`}>
                  <div className="p-6">
                    <div className="mb-4">
                      <p className="font-sans text-xs font-semibold text-stone-900 mb-1" data-testid="order-id">
                        {t("orders.transaction")}: <span className="text-[11px] tracking-wide break-all text-stone-700">{order.id}</span>
                      </p>
                      <p className="text-xs text-stone-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(order.transaction_date || order.created_at).toLocaleDateString(locale === "id" ? "id-ID" : "en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute:"2-digit" })}
                      </p>
                      {order.items?.find(i => i.stock_type === "preorder" && i.preorder_closing_date) && (
                        <POCountdown closingDate={order.items.find(i => i.stock_type === "preorder" && i.preorder_closing_date).preorder_closing_date} />
                      )}
                      <div className="mt-3 flex flex-wrap justify-start gap-2">
                        <Badge className={`${primaryStatusMeta.color} hover:bg-opacity-80 text-[10px] uppercase tracking-wider font-semibold py-1.5 px-3 rounded-lg`} data-testid="order-status">
                          {locale === "id" ? primaryStatusMeta.id : primaryStatusMeta.en}
                        </Badge>
                        {order.payment_status_v2 && (
                          <>
                            <Badge className={`${getPaymentStatusMeta(order.payment_status_v2, order).color} text-[10px] uppercase tracking-wider font-semibold py-1.5 px-2.5 rounded-md border-0`}>
                              Bayar: {locale === "id" ? getPaymentStatusMeta(order.payment_status_v2, order).id : getPaymentStatusMeta(order.payment_status_v2, order).en}
                            </Badge>
                            <Badge className={`${getOrderProgressMeta(order.order_status_v2).color} text-[10px] uppercase tracking-wider font-semibold py-1.5 px-2.5 rounded-md border-0`}>
                              Pesanan: {locale === "id" ? getOrderProgressMeta(order.order_status_v2).id : getOrderProgressMeta(order.order_status_v2).en}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4 bg-stone-50 p-4 rounded border border-stone-100">
                      {order.items?.map((item, i) => (
                        <div key={i} className="space-y-0.5">
                          <div className="flex justify-between text-sm items-center">
                            <span className="text-stone-700">
                               <span className="font-medium text-stone-900">
                                {getDisplayProductLabel(item.product_name, item.brand_name)}
                              </span> 
                              <span className="mx-2 text-stone-400 normal-case">× {item.quantity}</span>
                              {item.variant && Object.keys(item.variant).length > 0 && <span className="text-stone-500 text-xs">({getDisplayVariantValues(item.variant).join(', ')})</span>}
                              
                              {item.stock_type === 'preorder' && ['ready_at_central', 'ready_at_gallery'].includes(order.order_status_v2) && (
                                <span className="ml-2 inline-block bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider align-middle">
                                  {order.order_status_v2 === 'ready_at_gallery' ? 'Ready di Gallery ✅' : 'Barang Sudah Ready ✅'}
                                </span>
                              )}
                              {item.stock_type === 'preorder' && order.order_status_v2 === 'in_production' && (
                                <span className="ml-2 inline-block bg-indigo-100 text-indigo-800 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider align-middle">Sedang Diproduksi ✂️</span>
                              )}
                            </span>
                            <span className="text-right leading-tight">
                              {Number(item.discount_snapshot || 0) > 0 ? (
                                <>
                                  <span className="block text-[11px] text-stone-400 line-through">{formatPrice((Number(item.price_snapshot || item.price || 0) + Number(item.discount_snapshot || 0)) * Number(item.quantity || 1))}</span>
                                  <span className="block text-stone-800 font-medium">{formatPrice(item.item_total)}</span>
                                </>
                              ) : (
                                <span className="text-stone-800">{formatPrice(item.item_total)}</span>
                              )}
                            </span>
                          </div>
                          {item.item_notes && (
                            <p className="text-[11px] text-stone-400 italic pl-1">📝 {item.item_notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between items-center py-2 px-1 border-t border-stone-100 pt-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("orders.grand_total")}</span>
                        {orderDiscountTotal > 0 ? (
                          <>
                            <span className="block text-[11px] text-stone-400 line-through">{formatPrice(orderGrandTotalOriginal)}</span>
                            <span className="font-semibold text-lg text-emerald-600">{formatPrice(order.grand_total || order.total)}</span>
                          </>
                        ) : (
                          <span className="font-semibold text-lg text-emerald-600">{formatPrice(order.grand_total || order.total)}</span>
                        )}
                        {(shippingCost > 0 || shippingData.service_name || shippingData.courier_name || shippingData.courier) && (
                          <div className="text-xs text-stone-500 space-y-0.5 mt-1">
                            <div>Ongkir: <span className="font-medium text-stone-700">{formatPrice(shippingCost)}</span></div>
                            {(shippingData.courier_name || shippingData.courier || shippingData.service_name) && (
                              <div>{shippingData.courier_name || shippingData.courier}{shippingData.service_name ? ` · ${shippingData.service_name}` : ""}</div>
                            )}
                          </div>
                        )}
                        {order.payment_summary && (
                          <div className="text-xs text-stone-500 space-y-0.5 mt-1">
                            <div>Sudah dibayar: <span className="font-medium text-stone-700">{formatPrice(totalPaid)}</span></div>
                            {remaining > 0 && (
                              <div>Sisa tagihan: <span className="font-medium text-amber-700">{formatPrice(remaining)}</span></div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const res = await API.get(`/orders/${order.id}/invoice-pdf`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `invoice_${order.id}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch {
                              toast.error(locale === "id" ? "Gagal download invoice" : "Failed to download invoice");
                            }
                          }}
                          className="bg-stone-100 text-stone-600 px-3 py-2 text-sm hover:bg-stone-200 transition-colors flex items-center gap-1.5 rounded-md"
                          title="Download Invoice PDF"
                        >
                          <FileDown className="w-4 h-4" />
                          <span>Invoice</span>
                        </button>
                        {shouldShowShippingSetupButton && (
                          <Link
                            to={`/orders/${order.id}/shipping`}
                            className="bg-cyan-50 text-cyan-700 border border-cyan-200 px-3 py-2 text-sm hover:bg-cyan-100 transition-colors flex items-center gap-1.5 rounded-md"
                            title="Atur Pengiriman"
                          >
                            <SplitSquareVertical className="w-4 h-4" />
                            <span className="sm:hidden">Atur</span>
                            <span className="hidden sm:inline">Atur Pengiriman</span>
                          </Link>
                        )}
                        {canCompleteOrder && (
                          <button
                            onClick={() => handleCompleteOrder(order.id)}
                            disabled={completing === order.id}
                            className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 text-sm hover:bg-emerald-100 transition-colors flex items-center gap-1.5 rounded-md disabled:opacity-60"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span className="sm:hidden">Terima</span>
                            <span className="hidden sm:inline">Pesanan Diterima</span>
                          </button>
                        )}
                        <Link 
                          to={`/orders/${order.id}`}
                          className="bg-stone-900 text-white px-4 sm:px-5 py-2 text-sm uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors rounded-md"
                        >
                          {t("orders.view_detail")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                <p className="text-xs text-stone-500 text-center sm:text-left">
                  {locale === "id" ? "Halaman" : "Page"} {currentPage} / {totalPages} · {filteredOrders.length} {locale === "id" ? "pesanan" : "orders"}
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-stone-200 bg-white text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {locale === "id" ? "Sebelumnya" : "Prev"}
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                      .filter((page) => totalPages <= 7 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, index, visiblePages) => {
                        const prevPage = visiblePages[index - 1];
                        const showGap = prevPage && page - prevPage > 1;
                        return (
                          <div key={page} className="flex items-center gap-1">
                            {showGap && <span className="px-1 text-stone-400">…</span>}
                            <button
                              type="button"
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-9 px-3 py-2 rounded-md border text-sm font-semibold transition-colors ${currentPage === page ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`}
                            >
                              {page}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-stone-200 bg-white text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {locale === "id" ? "Berikutnya" : "Next"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
